/* ─────────────────────────────────────────────────────────────
   Places / Map page — immersive layout.
   Live Leaflet map re-skinned to the leather palette, bespoke
   monoline kind-pins, a floating index, fly-to focus, drop-a-pin.
   Wired to the real /api/places (label/lat/lon/kind + attached
   place-bound reminders).
   ───────────────────────────────────────────────────────────── */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import L from "leaflet";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Wordmark } from "../components/Wordmark";
import { api, type Place, type PlaceItem } from "../lib/api";
import { CATEGORY_META, Icon } from "../lib/icons";

import "leaflet/dist/leaflet.css";
import "./Places.css";

// Europe/Berlin — the app's timezone, a sane default before any place exists.
const DEFAULT_CENTER: [number, number] = [52.52, 13.405];

/* ── Place-type marks — bespoke monoline glyphs (Inbox style) ── */
const KIND_SVG: Record<string, string> = {
  pin: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="3.2" fill="currentColor" stroke="none"/></svg>',
  home: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 11.5l8-6.5 8 6.5"/><path d="M6.5 10.2V18.4h11V10.2"/></svg>',
  work: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="7" width="14" height="10" rx="1.5"/><path d="M9 7V5.7A1.5 1.5 0 0 1 10.5 4.2h3A1.5 1.5 0 0 1 15 5.7V7"/><path d="M5 11.4h14"/></svg>',
  food: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6.8 6.2h10.4l-1 10.4a1.5 1.5 0 0 1-1.5 1.35H9.3a1.5 1.5 0 0 1-1.5-1.35L6.8 6.2z"/><path d="M5.6 6.2h12.8"/></svg>',
  shop: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 8.5h12l-1 10.5H7L6 8.5z"/><path d="M9 8.5V6.8a3 3 0 0 1 6 0v1.7"/></svg>',
  venue: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 17.2V6l8-1.8v9.4"/><ellipse cx="8" cy="17.2" rx="2.1" ry="1.9"/><ellipse cx="16" cy="15.1" rx="2.1" ry="1.9"/></svg>',
  active:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 17l5-10 3 4.8 2-3.2 5 8.4z"/></svg>',
  friend:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19.6l-1.3-1.18C6 14.1 3 11.4 3 8.1A4.1 4.1 0 0 1 7.1 4c1.6 0 3.1.74 4.1 1.94A5.46 5.46 0 0 1 15.3 4 4.1 4.1 0 0 1 19.4 8.1c0 3.3-3 6-7.7 10.32L12 19.6z"/></svg>',
};
const KIND_LABEL: Record<string, string> = {
  pin: "place",
  home: "home",
  work: "work",
  food: "café",
  shop: "shop",
  venue: "venue",
  active: "active",
  friend: "friend",
};
const KINDS = ["home", "work", "friend", "food", "shop", "venue", "active", "pin"];

const CATS = ["reminder", "task", "note", "link", "event"];

function kindSvg(kind: string): string {
  return KIND_SVG[kind] || KIND_SVG.pin;
}

function KindGlyph({ kind, className }: { kind: string; className?: string }) {
  return (
    <span
      className={"kind-glyph " + (className || "")}
      dangerouslySetInnerHTML={{ __html: kindSvg(kind) }}
    />
  );
}

// the categories of capture present at a place, in canonical order
function placeCats(p: Place): string[] {
  return CATS.filter((c) => p.items.some((it) => it.category === c));
}

// divIcon html is set via innerHTML — escape user-supplied strings.
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/* divIcon factory — a neutral place pin marked by its kind */
function makePinIcon(p: Place): L.DivIcon {
  const html =
    `<div class="pin" data-kind="${escapeHtml(p.kind || "pin")}">` +
    `<span class="pin-head">${kindSvg(p.kind)}</span>` +
    `<span class="pin-label">${escapeHtml(p.label)}</span>` +
    `</div>`;
  return L.divIcon({ className: "pin-wrap", html, iconSize: [34, 34], iconAnchor: [17, 17] });
}

function draftIcon(): L.DivIcon {
  return L.divIcon({
    className: "pin-wrap is-draft",
    html: `<div class="pin"><span class="pin-head"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14"/></svg></span></div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  });
}

/* ── Attached captures (what lives at a place) ────────────────── */
function AttachedList({ p }: { p: Place }) {
  if (!p.items.length) {
    return (
      <p className="att-empty">
        Nothing tied here yet. Send the bot a reminder for when you’re at{" "}
        <em>{p.label}</em>.
      </p>
    );
  }
  return (
    <div className="attached">
      <p className="att-label">
        tied here<span className="att-count">{p.items.length}</span>
      </p>
      <ul className="att-list">
        {p.items.map((it: PlaceItem) => {
          const meta = CATEGORY_META[it.category];
          const IconCmp = meta?.icon ?? Icon.note;
          return (
            <li
              key={it.id}
              className="att-row"
              style={{ ["--cat-color" as string]: meta?.color ?? "var(--text-muted)" }}
            >
              <span className="att-ico">
                <IconCmp />
              </span>
              <span className="att-summary">{it.summary}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ── One floating-index row ───────────────────────────────────── */
function PlaceRow({
  p,
  active,
  hover,
  onHover,
  onSelect,
}: {
  p: Place;
  active: boolean;
  hover: boolean;
  onHover: (id: number | null) => void;
  onSelect: (id: number) => void;
}) {
  const cats = placeCats(p);
  return (
    <li
      className={"place-row" + (active ? " is-active" : "") + (hover && !active ? " is-hover" : "")}
      role="button"
      tabIndex={0}
      onMouseEnter={() => onHover(p.id)}
      onMouseLeave={() => onHover(null)}
      onClick={() => onSelect(p.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(p.id);
        }
      }}
    >
      <span className="place-ico">
        <KindGlyph kind={p.kind} />
      </span>
      <div className="place-main">
        <div className="place-label">{p.label}</div>
      </div>
      <span className="place-meta-right">
        {cats.length > 0 && (
          <span className="att-dots">
            {cats.map((c) => (
              <i key={c} style={{ background: CATEGORY_META[c]?.color }} />
            ))}
          </span>
        )}
        <span className="att-n">{p.items.length || "—"}</span>
      </span>
    </li>
  );
}

/* ── Main page ────────────────────────────────────────────────── */
export function Places() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["places"], queryFn: api.listPlaces });
  const places = useMemo(() => q.data?.items ?? [], [q.data]);

  const [cats, setCats] = useState<string[]>([]); // [] = all
  const [hoverId, setHoverId] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<{ lat: number; lon: number } | null>(null);
  const [draftLabel, setDraftLabel] = useState("");
  const [draftKind, setDraftKind] = useState("pin");
  const [draftAddress, setDraftAddress] = useState(""); // alt to a map click: geocoded to a preview pin
  const [geoState, setGeoState] = useState<"idle" | "loading" | "miss">("idle");
  const [markOpen, setMarkOpen] = useState(false); // detail-card mark picker, collapsed by default

  const mapEl = useRef<HTMLDivElement | null>(null);
  const mapInst = useRef<L.Map | null>(null);
  const markers = useRef<Record<number, L.Marker>>({});
  const draftMarker = useRef<L.Marker | null>(null);
  const addingRef = useRef(false);
  const flyFallback = useRef<ReturnType<typeof setTimeout> | null>(null);
  const framed = useRef(false);
  const geocodeSeq = useRef(0); // guards against out-of-order address lookups
  const draftFromAddr = useRef(false); // is the current draft pin from a geocoded address?
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    addingRef.current = adding;
  }, [adding]);

  const visible = useMemo(
    () => places.filter((p) => cats.length === 0 || p.items.some((it) => cats.includes(it.category))),
    [places, cats],
  );
  const counts = useMemo(() => {
    const c: Record<string, number> = { reminder: 0, task: 0, note: 0, link: 0 };
    for (const p of places) for (const cat of placeCats(p)) c[cat]++;
    return c;
  }, [places]);
  const selected = useMemo(
    () => places.find((p) => p.id === selectedId) || null,
    [places, selectedId],
  );

  /* ── Select → fly to ───────────────────────────────────────── */
  const selectPlace = useCallback((id: number) => {
    setSelectedId(id);
    const map = mapInst.current;
    const m = markers.current[id];
    if (!map || !m) return;
    const ll = m.getLatLng();
    const targetZoom = Math.max(map.getZoom(), 15.5);
    map.flyTo(ll, targetZoom, { duration: 0.85 });
    // Safety: guarantee arrival even where rAF is throttled (background tab).
    if (flyFallback.current) clearTimeout(flyFallback.current);
    flyFallback.current = setTimeout(() => {
      if (
        map.distance(map.getCenter(), ll) > 40 ||
        Math.abs(map.getZoom() - targetZoom) > 0.05
      ) {
        map.setView(ll, targetZoom, { animate: false });
      }
    }, 1150);
  }, []);

  /* ── Init the map once ─────────────────────────────────────── */
  useEffect(() => {
    if (mapInst.current || !mapEl.current) return;
    const map = L.map(mapEl.current, {
      center: DEFAULT_CENTER,
      zoom: 13,
      zoomControl: false,
      attributionControl: true,
      scrollWheelZoom: true,
      zoomSnap: 0.25,
    });
    mapInst.current = map;
    L.control.zoom({ position: "topright" }).addTo(map);
    // Esri's Dark Gray canvas only has tiles to z16; maxNativeZoom lets Leaflet
    // upscale the z16 tiles past that instead of requesting 404s (blank map).
    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
      {
        maxZoom: 19,
        maxNativeZoom: 16,
        crossOrigin: "anonymous",
        attribution:
          "Tiles &copy; Esri — Esri, HERE, Garmin, &copy; OpenStreetMap contributors",
      },
    ).addTo(map);
    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}",
      { maxZoom: 19, maxNativeZoom: 16, crossOrigin: "anonymous" },
    ).addTo(map);

    map.on("click", (e: L.LeafletMouseEvent) => {
      if (!addingRef.current) return;
      draftFromAddr.current = false;
      setDraft({ lat: e.latlng.lat, lon: e.latlng.lng });
      setDraftAddress(""); // a map click is the location source — drop any typed address
    });

    setMapReady(true);
    return () => {
      if (flyFallback.current) clearTimeout(flyFallback.current);
      map.remove();
      mapInst.current = null;
    };
  }, []);

  /* ── Frame on the user's places, once they've loaded ───────── */
  useEffect(() => {
    if (!mapReady || framed.current || places.length === 0) return;
    framed.current = true;
    const map = mapInst.current!;
    if (places.length === 1) {
      map.setView([places[0].lat, places[0].lon], 14);
    } else {
      map.fitBounds(
        places.map((p) => [p.lat, p.lon] as [number, number]),
        { padding: [56, 56] },
      );
    }
  }, [places, mapReady]);

  /* ── Reconcile markers with `places` ───────────────────────── */
  useEffect(() => {
    if (!mapReady) return;
    const map = mapInst.current!;
    const seen = new Set<number>();
    for (const p of places) {
      seen.add(p.id);
      if (!markers.current[p.id]) {
        const m = L.marker([p.lat, p.lon], { icon: makePinIcon(p), riseOnHover: true });
        m.on("mouseover", () => setHoverId(p.id));
        m.on("mouseout", () => setHoverId(null));
        m.on("click", () => selectPlace(p.id));
        markers.current[p.id] = m;
      } else {
        markers.current[p.id].setIcon(makePinIcon(p));
      }
    }
    for (const id of Object.keys(markers.current).map(Number)) {
      if (!seen.has(id)) {
        map.removeLayer(markers.current[id]);
        delete markers.current[id];
      }
    }
  }, [places, mapReady, selectPlace]);

  /* ── Filter: membership on the map ─────────────────────────── */
  useEffect(() => {
    if (!mapReady) return;
    const map = mapInst.current!;
    const vis = new Set(visible.map((p) => p.id));
    for (const id of Object.keys(markers.current).map(Number)) {
      const m = markers.current[id];
      if (vis.has(id)) {
        if (!map.hasLayer(m)) m.addTo(map);
      } else if (map.hasLayer(m)) map.removeLayer(m);
    }
  }, [visible, mapReady]);

  /* ── Hover / active classes on pin elements ────────────────── */
  useEffect(() => {
    for (const id of Object.keys(markers.current).map(Number)) {
      const el = markers.current[id].getElement();
      if (!el) continue;
      el.classList.toggle("is-hover", id === hoverId);
      el.classList.toggle("is-active", id === selectedId);
      markers.current[id].setZIndexOffset(id === selectedId ? 1000 : id === hoverId ? 600 : 0);
    }
  }, [hoverId, selectedId, visible, mapReady]);

  /* ── Draft marker ──────────────────────────────────────────── */
  useEffect(() => {
    const map = mapInst.current;
    if (!map) return;
    if (draft) {
      if (!draftMarker.current) {
        draftMarker.current = L.marker([draft.lat, draft.lon], {
          icon: draftIcon(),
          zIndexOffset: 1200,
        }).addTo(map);
      } else {
        draftMarker.current.setLatLng([draft.lat, draft.lon]);
      }
    } else if (draftMarker.current) {
      map.removeLayer(draftMarker.current);
      draftMarker.current = null;
    }
  }, [draft]);

  /* ── Address → preview pin (debounced; same geocoder as save) ─ */
  useEffect(() => {
    // Bump first so clearing/cancelling supersedes any lookup already in flight.
    const seq = ++geocodeSeq.current;
    const q = draftAddress.trim();
    if (!q) {
      // Cleared the field: drop the preview pin if that's where it came from.
      if (draftFromAddr.current) {
        setDraft(null);
        draftFromAddr.current = false;
      }
      setGeoState("idle");
      return;
    }
    setGeoState("loading");
    const t = setTimeout(async () => {
      try {
        const hit = await api.geocodePreview(q);
        if (seq !== geocodeSeq.current) return; // a newer keystroke superseded this
        draftFromAddr.current = true;
        setDraft({ lat: hit.lat, lon: hit.lon });
        setGeoState("idle");
        const map = mapInst.current;
        if (map) map.flyTo([hit.lat, hit.lon], Math.max(map.getZoom(), 15), { duration: 0.8 });
      } catch {
        if (seq !== geocodeSeq.current) return;
        if (draftFromAddr.current) {
          setDraft(null);
          draftFromAddr.current = false;
        }
        setGeoState("miss");
      }
    }, 600);
    return () => clearTimeout(t);
  }, [draftAddress]);

  /* ── Mutations ─────────────────────────────────────────────── */
  const add = useMutation({
    mutationFn: () =>
      api.addPlace({ label: draftLabel.trim(), kind: draftKind, lat: draft!.lat, lon: draft!.lon }),
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ["places"] });
      setDraft(null);
      setDraftLabel("");
      setDraftKind("pin");
      setDraftAddress("");
      setAdding(false);
      setTimeout(() => selectPlace(saved.id), 80);
    },
  });
  const del = useMutation({
    mutationFn: (id: number) => api.deletePlace(id),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ["places"] });
      setSelectedId((s) => (s === id ? null : s));
    },
  });
  // Change a place's mark: re-save the same label/coords with a new kind (the
  // backend upserts on the normalized label, so this updates in place).
  const setKind = useMutation({
    mutationFn: ({ p, kind }: { p: Place; kind: string }) =>
      api.addPlace({ label: p.label, kind, lat: p.lat, lon: p.lon }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["places"] }),
  });

  // A freshly selected place starts with its mark picker collapsed.
  useEffect(() => setMarkOpen(false), [selectedId]);

  const onHover = useCallback((id: number | null) => setHoverId(id), []);
  const toggleCat = (c: string) => {
    if (c === "__all") return setCats([]);
    setCats((cur) => (cur[0] === c && cur.length === 1 ? [] : [c]));
  };
  const startAdding = () => {
    if (adding) {
      setAdding(false);
      setDraft(null);
      setDraftLabel("");
      setDraftAddress("");
      return;
    }
    setAdding(true);
    setSelectedId(null);
  };
  const canSave = draft !== null && draftLabel.trim().length > 0 && !add.isPending;

  /* Esc cancels adding / clears selection */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (adding) {
        setAdding(false);
        setDraft(null);
        setDraftLabel("");
        setDraftAddress("");
      } else if (selectedId) setSelectedId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [adding, selectedId]);

  const railList = (
    <ul className="rail-list">
      {visible.length === 0 ? (
        <li className="rail-empty">No places hold one of those. The map is at rest.</li>
      ) : (
        visible.map((p) => (
          <PlaceRow
            key={p.id}
            p={p}
            active={p.id === selectedId}
            hover={p.id === hoverId}
            onHover={onHover}
            onSelect={selectPlace}
          />
        ))
      )}
    </ul>
  );

  return (
    <div className="mappage" data-adding={adding ? "true" : "false"}>
      <header className="header">
        <div className="map-shell">
          <div className="header-inner">
            <button className="back-link" onClick={() => navigate("/")}>
              <Icon.back /> timeline
            </button>
            <div style={{ flex: 1 }} />
            <Wordmark />
          </div>
        </div>
      </header>

      <main className="map-shell" style={{ paddingBottom: 40 }}>
        <div className="title-row">
          <div className="lede">
            <div className="page-title">
              <span>places</span>
              <span className="n">{places.length}</span>
            </div>
            <p className="page-lede">
              The places you’ve named — each one backs a “remind me when I’m at …”. Hover to find it
              on the map; open one to fly there.
            </p>
          </div>
          <div className="title-actions">
            <button className="ghost-btn" data-on={adding ? "true" : "false"} onClick={startAdding}>
              {adding ? (
                "cancel"
              ) : (
                <>
                  <span className="plus">＋</span> drop a pin
                </>
              )}
            </button>
          </div>
        </div>

        <div className="places-chips">
          <div className="chips" role="group" aria-label="Filter places">
            <button className="chip" aria-pressed={cats.length === 0} onClick={() => toggleCat("__all")}>
              <span style={{ color: "var(--text-muted)" }}>All</span>
              <span className="count">{places.length}</span>
            </button>
            {/* Only categories that actually tie to a place — today reminders.
                A chip with a 0 count would just blank the map when tapped. */}
            {CATS.filter((c) => counts[c] > 0).map((c) => {
              const meta = CATEGORY_META[c];
              return (
                <button
                  key={c}
                  className="chip"
                  aria-pressed={cats.includes(c)}
                  style={{ ["--chip-color" as string]: meta.color }}
                  onClick={() => toggleCat(c)}
                >
                  <span className="dot" />
                  <span>{meta.plural}</span>
                  <span className="count">{counts[c] || 0}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="map-stage">
          <div className="map-canvas" ref={mapEl} />

          {/* Floating index */}
          <div className="float-index">
            <div className="rail-head">
              <span>the index</span>
              <span className="n">{visible.length}</span>
            </div>
            {railList}
          </div>

          {/* Detail card */}
          <div className={"detail-card" + (selected ? " show" : "")}>
            {selected ? (
              <>
                <div className="dc-eyebrow">
                  <KindGlyph kind={selected.kind} className="dc-kind" />
                  <span>{KIND_LABEL[selected.kind] || "place"}</span>
                  <button className="close" onClick={() => setSelectedId(null)} aria-label="Close">
                    ✕
                  </button>
                </div>
                <div className="dc-title">{selected.label}</div>
                <div className="dc-marks">
                  {markOpen ? (
                    <div className="draft-kinds" role="group" aria-label="Change mark">
                      {KINDS.map((k) => (
                        <button
                          key={k}
                          className="kind-chip"
                          aria-pressed={selected.kind === k}
                          title={KIND_LABEL[k]}
                          disabled={setKind.isPending}
                          onClick={() => {
                            setKind.mutate({ p: selected, kind: k });
                            setMarkOpen(false);
                          }}
                        >
                          <KindGlyph kind={k} />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <button className="mini-link" onClick={() => setMarkOpen(true)}>
                      change mark
                    </button>
                  )}
                </div>
                <AttachedList p={selected} />
                <div className="coords">
                  {selected.lat.toFixed(5)}, {selected.lon.toFixed(5)}
                </div>
                <div className="place-actions" style={{ marginTop: 14 }}>
                  <button
                    className="mini-link danger"
                    onClick={() => {
                      if (confirm(`Remove place “${selected.label}”?`)) del.mutate(selected.id);
                    }}
                  >
                    remove place
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>

        {/* Drop-a-pin naming bar */}
        {adding ? (
          <div className="draft-bar">
            <p className="hint">
              {geoState === "loading"
                ? "Looking up that address…"
                : geoState === "miss"
                  ? "Couldn’t find that address — try a fuller one."
                  : draft
                    ? "Name this place, choose a mark, and save."
                    : "Click the map to drop a pin, or paste an address — then name it."}
            </p>
            <input
              className="place-input"
              autoFocus
              placeholder="home, the good bakery, mum’s…"
              value={draftLabel}
              onChange={(e) => setDraftLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSave) add.mutate();
              }}
            />
            <input
              className="place-input"
              placeholder="…or paste an address"
              value={draftAddress}
              onChange={(e) => setDraftAddress(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSave) add.mutate();
              }}
            />
            <div className="draft-kinds" role="group" aria-label="Place type">
              {KINDS.map((k) => (
                <button
                  key={k}
                  className="kind-chip"
                  aria-pressed={draftKind === k}
                  title={KIND_LABEL[k]}
                  onClick={() => setDraftKind(k)}
                >
                  <KindGlyph kind={k} />
                </button>
              ))}
            </div>
            <button className="save-btn" disabled={!canSave} onClick={() => add.mutate()}>
              {add.isPending ? "Saving…" : "Save"}
            </button>
          </div>
        ) : null}
        {add.isError ? (
          <p style={{ color: "oklch(64% 0.15 25)", fontSize: 13, marginTop: 8 }}>
            Couldn’t save that place. Try again.
          </p>
        ) : null}
      </main>
    </div>
  );
}
